"use client";

import type { ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { DiscountCodeForm, TotalPriceOff } from "@/features/cart/DiscountCodeForm";
import { useCart } from "@/features/cart/hooks";
import { useTranslations } from "@/i18n/client";

export interface CartSummaryProps {
	variant?: "checkout" | "default";
}

export const CartSummary = ({ variant = "default" }: CartSummaryProps) => {
	const { data } = useCart();

	const t = useTranslations("cart");

	if (!data?.hasItems) {
		return null;
	}

	const isFreeShipping = !data.shippingPriceAsNumber || data.shippingPriceAsNumber === 0;
	const shippingValue = isFreeShipping ? t("free") : data.shippingPrice;

	const totalPriceOff = (data?.vouchers ?? []).reduce((acc, voucher) => {
		return acc + (voucher.priceOffAsNumber ?? 0) + (voucher.shippingDiscountAsNumber ?? 0);
	}, 0);
	const hasDiscount = totalPriceOff < 0;

	const breakdownRows = (
		<>
			<Row label={t("subtotal")} value={data.subtotal} />
			{variant === "checkout" && hasDiscount && (
				<Row label={t("discountCode")} value={<TotalPriceOff discount={totalPriceOff} />} />
			)}
			<Row
				label={t("shipping")}
				value={shippingValue}
				valueClassName={isFreeShipping ? "text-status-success-text" : undefined}
			/>
			<div className="flex flex-col gap-grid-component-spacing-5xs">
				<div className="flex items-end justify-between font-body font-regular text-base leading-[1.5] text-text-dark-text-1">
					<span>{t("total")}</span>
					<span className="text-right">{data.total}</span>
				</div>
				{data.vat && (
					<div className="flex items-end justify-between font-body font-regular text-2xs leading-[1.5] text-text-dark-text-2">
						<span>{t("vat")}</span>
						<span className="text-right">{data.vat}</span>
					</div>
				)}
			</div>
		</>
	);

	if (variant === "checkout") {
		return (
			<>
				<div className="flex flex-col gap-grid-component-spacing-md py-grid-component-spacing-md border-y border-border-medium">
					{breakdownRows}
				</div>
				{!hasDiscount && (
					<div className="py-grid-component-spacing-md border-b border-border-medium">
						<DiscountCodeForm />
					</div>
				)}
			</>
		);
	}

	return (
		<div className="flex flex-col gap-grid-component-spacing-md py-grid-component-spacing-md border-y border-border-medium">
			{breakdownRows}
			<Button as="a" href="/checkout" size="large" color="primary">
				{t("checkout")}
			</Button>
		</div>
	);
};

const Row = ({
	label,
	value,
	valueClassName,
}: {
	label: string;
	value: string | ReactNode | undefined;
	valueClassName?: string;
}) => (
	<div className="flex items-end justify-between font-body font-regular text-xs leading-[1.15] text-text-dark-text-1 uppercase tabular-nums lining-nums">
		<span>{label}</span>
		<span className={`text-right ${valueClassName ?? ""}`}>{value}</span>
	</div>
);
